"use client";

import { usePathname } from "next/navigation";
import { UserProvider } from "./_providers/UserProvider/UserProvider";
import { AlertProvider } from "./_providers/AlertProvider/AlertProvider";
import { StripeAccountProvider } from "./_providers/StripeAccountProvider";
import { StripeTerminalProvider } from "./_providers/StripeTerminalProvider";
import { NotificationsProvider } from "./_providers/NotificationsProvider/NotificationsProvider";
import { TagsProvider } from "./_providers/TagsProvider/TagsProvider";
import { SubscriptionProvider } from "./_providers/SubscriptionProvider/SubscriptionProvider";
import { PermissionsProvider } from "./_providers/PermissionsProvider/PermissionsProvider";
import { PrinterProvider } from "./_providers/PrinterProvider/PrinterProvider";
import { TimeClockProvider } from "./_providers/TimeClockProvider/TimeClockProvider";
import { AccountingProvider } from "./_providers/AccountingProvider/AccountingProvider";
import { FiltersProvider } from "./_providers/FiltersProvider/FiltersProvider";
import Layout from "./LayoutComponent";
import { Analytics } from "@vercel/analytics/react";
import { useEffect } from "react";
import { installConsoleErrorTap } from "./_helpers/errorBreadcrumbs";
import { useReloadOnNewBuild } from "./_hooks/useReloadOnNewBuild";

export default function ClientWrapper({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();

  useReloadOnNewBuild();

  useEffect(() => {
    installConsoleErrorTap();
  }, []);

  return pathname?.includes("/book/") ? (
    // Minimal providers for booking pages
    <AlertProvider>
      {children}
      {process.env.NODE_ENV === "production" && <Analytics />}
    </AlertProvider>
  ) : (
    // Full provider stack for authenticated pages
    <UserProvider>
      <AlertProvider>
        <StripeAccountProvider>
          <StripeTerminalProvider>
            <NotificationsProvider>
              <TagsProvider>
                <SubscriptionProvider>
                  <PermissionsProvider>
                    <PrinterProvider>
                      <AccountingProvider>
                        <FiltersProvider>
                          <TimeClockProvider>
                            <Layout>
                              {children}
                              {process.env.NODE_ENV === "production" && (
                                <Analytics />
                              )}
                            </Layout>
                          </TimeClockProvider>
                        </FiltersProvider>
                      </AccountingProvider>
                    </PrinterProvider>
                  </PermissionsProvider>
                </SubscriptionProvider>
              </TagsProvider>
            </NotificationsProvider>
          </StripeTerminalProvider>
        </StripeAccountProvider>
      </AlertProvider>
    </UserProvider>
  );
}
